import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { trackCTAClick } from '@/lib/analytics';

const stats = [
{ icon: 'TrophyIcon', label: '18 Holes', sub: 'Par 72' },
{ icon: 'MapPinIcon', label: 'PMB, KZN', sub: 'New England Rd' },
{ icon: 'StarIcon', label: 'Est. 1886', sub: "SA\'s Oldest Playing Club" },
{ icon: 'ClockIcon', label: 'Open Daily', sub: 'From 6:00 AM' }];


const socialProof = [
{ value: '1886', label: "Year founded — SA\'s oldest playing golf club" },
{ value: '4.9★', label: 'Visitor rating' },
{ value: '18', label: 'Championship holes' }];


export default function HeroSection() {
  return (
    <section className="relative overflow-hidden gradient-primary min-h-[620px] flex items-center">
      {/* Background image overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <AppImage
          src="https://img.rocket.new/generatedImages/rocket_gen_img_195057554-1768069416502.png"
          alt="Maritzburg Golf Club championship course aerial view"
          fill
          className="object-cover opacity-20"
          priority />
        
        <div className="absolute inset-0 bg-primary/70" />
        <div className="absolute top-8 left-8 w-72 h-72 rounded-full border border-white/10" />
        <div className="absolute bottom-8 right-16 w-[28rem] h-[28rem] rounded-full border border-white/8" />
        <div className="absolute top-1/4 right-1/3 w-20 h-20 rounded-full bg-accent/10" />
      </div>

      <div className="relative max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-20 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left: main copy */}
          <div className="flex-1 max-w-2xl">
            {/* Location badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span className="text-white/90 text-sm font-medium">Pietermaritzburg, KwaZulu-Natal</span>
            </div>

            <h1 className="text-4xl md:text-5xl xl:text-6xl font-extrabold text-white leading-tight mb-5 text-balance">
              A Legacy of Golf<br />
              Since <span className="text-accent">1886</span>
            </h1>

            <p className="text-white/70 text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
              Experience an 18-hole championship course, a warm community spirit, and over a century of golfing tradition at Maritzburg Golf Club — South Africa&apos;s oldest playing golf club.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 mb-10">
              <Link
                href="/online-booking"
                onClick={() => trackCTAClick('Book a Round', 'hero')}
                className="btn-gold px-8 py-3.5 rounded-xl text-base font-bold inline-flex items-center gap-2 shadow-[0_4px_20px_rgba(212,175,55,0.35)]">
                
                <Icon name="CalendarDaysIcon" size={20} variant="outline" />
                Book a Round
              </Link>
              <Link
                href="#pricing"
                className="px-8 py-3.5 rounded-xl text-base font-semibold text-white border-2 border-white/30 hover:border-white/60 hover:bg-white/10 transition-all duration-200 inline-flex items-center gap-2">
                
                <span className="text-base font-bold leading-none">R</span>
                View Pricing
              </Link>
            </div>

            {/* Course quick-stats */}
            <div className="flex flex-wrap gap-6">
              {stats.map((item) =>
              <div key={`hero-stat-${item.label}`} className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={18} variant="outline" className="text-accent" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm leading-none">{item.label}</div>
                    <div className="text-white/50 text-xs mt-0.5">{item.sub}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: social proof card */}
          <div className="shrink-0 w-full lg:w-64">
            <div className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-sm">
              <div className="text-white/60 text-xs uppercase tracking-widest font-semibold mb-5">Trusted by Golfers</div>
              <div className="space-y-5">
                {socialProof.map((s) =>
                <div key={s.label} className="flex items-center gap-4">
                    <div className="text-2xl font-extrabold text-accent tabular-nums">{s.value}</div>
                    <div className="text-white/70 text-sm leading-tight">{s.label}</div>
                  </div>
                )}
              </div>
              <div className="mt-6 pt-5 border-t border-white/10">
                <Link
                  href="/visitor-registration-sign-up-login"
                  className="w-full text-center py-2.5 rounded-xl text-sm font-semibold text-white border border-white/30 hover:bg-white/10 transition-all duration-150 block">
                  
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}